<template>
  <div class="p-0">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Concepto profesional</h1>
      <span
        class="px-3 py-1 text-sm font-medium rounded-full"
        :class="roleBadgeClass"
      >
        Rol actual: {{ roleLabel }}
      </span>
    </div>

    <!-- Vista Teacher: tabla de conceptos solicitados -->
    <template v-if="isTeacher">
      <div v-if="loading" class="text-center py-8 text-gray-600">
        Cargando conceptos...
      </div>
      <div v-else-if="loadError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p class="text-red-600">{{ loadError }}</p>
      </div>
      <div v-else>
        <!-- Botón solicitar concepto profesional -->
        <div class="mb-6">
          <button
            type="button"
            @click="openRequestConceptModal"
            :disabled="hasConceptForPreviousYear"
            :class="[
              'px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
              hasConceptForPreviousYear
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            ]"
          >
            Solicitar concepto profesional {{ previousYear }}
          </button>
        </div>

        <h2 class="text-lg font-semibold text-gray-800 mb-4">Conceptos anuales</h2>
        <div v-if="!yearlyConcepts.length" class="text-center py-8 text-gray-500">
          No hay conceptos anuales registrados.
        </div>
        <div v-else class="overflow-x-auto border border-gray-200 rounded-lg">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Año</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de solicitud</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="item in yearlyConcepts"
                :key="item.id"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-3 text-sm text-gray-900">{{ item.year || '-' }}</td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ formatDate(item.created_at) }}</td>
                <td class="px-4 py-3">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="getStatusClass(item.status)"
                  >
                    {{ statusLabel(item.status) }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      @click="viewConceptDetails(item)"
                      class="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                      title="Ver detalles"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="openEditConceptModal(item)"
                      :disabled="!canEditConcept(item)"
                      :class="[
                        'p-2 rounded-md transition-colors',
                        canEditConcept(item)
                          ? 'text-amber-600 hover:bg-amber-50 cursor-pointer'
                          : 'text-gray-300 cursor-not-allowed'
                      ]"
                      :title="canEditConcept(item) ? 'Editar concepto' : 'Solo se puede editar si el año del concepto es el año anterior'"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="downloadConceptPDF(item)"
                      :disabled="!canDownloadPDF(item)"
                      :class="[
                        'p-2 rounded-md transition-colors',
                        canDownloadPDF(item)
                          ? 'text-green-600 hover:bg-green-50 cursor-pointer'
                          : 'text-gray-300 cursor-not-allowed'
                      ]"
                      :title="canDownloadPDF(item) ? 'Descargar PDF' : 'PDF disponible solo cuando el concepto está listo'"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Ver detalles del concepto -->
      <div v-if="showViewConceptModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
        <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div class="flex items-center gap-3">
              <h2 class="text-xl font-semibold text-gray-800">Detalles del concepto</h2>
              <span
                v-if="selectedConcept"
                class="px-2.5 py-1 text-xs font-medium rounded-full"
                :class="getStatusClass(selectedConcept.status)"
              >
                {{ statusLabel(selectedConcept.status) }}
              </span>
            </div>
            <button
              type="button"
              @click="closeViewConceptModal"
              class="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div v-if="selectedConcept" class="p-6 space-y-5">
            <!-- Información general -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Información general</h3>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                <div>
                  <dt class="text-xs text-gray-500">Año</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.year || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Fecha de solicitud</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ formatDate(selectedConcept.created_at) }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Concepto</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.concept || '-' }}</dd>
                </div>
                <div class="col-span-2 sm:col-span-3">
                  <dt class="text-xs text-gray-500">Título</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.title || '-' }}</dd>
                </div>
              </div>
            </div>

            <!-- Datos del cargo -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Datos del cargo</h3>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                <div>
                  <dt class="text-xs text-gray-500">Cargo</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.position || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Tipo</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.type || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Carga horaria</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.workload || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Tiempo en el cargo</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.length_office || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Antigüedad docente</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.seniority_teacher || '-' }}</dd>
                </div>
              </div>
            </div>

            <!-- Fechas -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Fechas</h3>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
                <div>
                  <dt class="text-xs text-gray-500">Fecha de ingreso</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.date_admission || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Ingreso a la institución</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.date_admission_institution || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Fecha de finalización</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.date_finish || '-' }}</dd>
                </div>
              </div>
            </div>

            <!-- Formación -->
            <div class="bg-gray-50 rounded-lg p-4">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Formación</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                <div>
                  <dt class="text-xs text-gray-500">Posgrado / Título</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.postitle || '-' }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-gray-500">Posgrado</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.postgraduate || '-' }}</dd>
                </div>
                <div class="sm:col-span-2">
                  <dt class="text-xs text-gray-500">Otras formaciones</dt>
                  <dd class="text-sm font-medium text-gray-900 mt-0.5">{{ selectedConcept.other_trainings || '-' }}</dd>
                </div>
              </div>
            </div>

            <!-- Enlace -->
            <div v-if="selectedConcept.link" class="bg-gray-50 rounded-lg p-4">
              <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Enlace</h3>
              <a
                :href="selectedConcept.link"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-indigo-600 hover:text-indigo-800 underline break-all"
              >
                {{ selectedConcept.link }}
              </a>
            </div>

            <div class="pt-2 flex justify-end border-t border-gray-200">
              <button
                type="button"
                @click="closeViewConceptModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Editar concepto -->
      <div v-if="showEditConceptModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
        <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 class="text-xl font-semibold text-gray-800">Editar concepto</h2>
            <button
              type="button"
              @click="closeEditConceptModal"
              class="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form @submit.prevent="submitEditConcept" class="p-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input
                  v-model="conceptForm.title"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Profesor de Matemática"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cargo *</label>
                <input
                  v-model="conceptForm.position"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Docente"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <input
                  v-model="conceptForm.type"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Titular"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Carga horaria *</label>
                <input
                  v-model="conceptForm.workload"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. 20 hs"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Tiempo en el cargo *</label>
                <input
                  v-model="conceptForm.length_office"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. 5 años"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Antigüedad docente *</label>
                <input
                  v-model="conceptForm.seniority_teacher"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. 10 años"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de ingreso *</label>
                <input
                  v-model="conceptForm.date_admission"
                  type="date"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de ingreso a la institución *</label>
                <input
                  v-model="conceptForm.date_admission_institution"
                  type="date"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de finalización</label>
                <input
                  v-model="conceptForm.date_finish"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Posgrado / Título</label>
                <input
                  v-model="conceptForm.postitle"
                  type="text"
                  maxlength="191"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Especialización en Educación"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Posgrado</label>
                <input
                  v-model="conceptForm.postgraduate"
                  type="text"
                  maxlength="191"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Otras formaciones</label>
                <input
                  v-model="conceptForm.other_trainings"
                  type="text"
                  maxlength="191"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div v-if="conceptError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p class="text-sm text-red-600">{{ conceptError }}</p>
            </div>
            <div v-if="conceptSuccess" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p class="text-sm text-green-600">{{ conceptSuccess }}</p>
            </div>
            <div class="flex justify-end gap-3">
              <button
                type="button"
                @click="closeEditConceptModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="submittingConcept"
                class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="submittingConcept">Guardando...</span>
                <span v-else>Guardar cambios</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal Solicitar concepto profesional -->
      <div v-if="showRequestConceptModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
        <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 class="text-xl font-semibold text-gray-800">Solicitar concepto profesional</h2>
            <button
              type="button"
              @click="closeRequestConceptModal"
              class="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form @submit.prevent="submitUserConcept" class="p-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                <input
                  v-model="conceptForm.title"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Profesor de Matemática"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cargo *</label>
                <input
                  v-model="conceptForm.position"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Docente"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                <input
                  v-model="conceptForm.type"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Titular"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Carga horaria *</label>
                <input
                  v-model="conceptForm.workload"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. 20 hs"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Tiempo en el cargo *</label>
                <input
                  v-model="conceptForm.length_office"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. 5 años"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Antigüedad docente *</label>
                <input
                  v-model="conceptForm.seniority_teacher"
                  type="text"
                  maxlength="191"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. 10 años"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de ingreso *</label>
                <input
                  v-model="conceptForm.date_admission"
                  type="date"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de ingreso a la institución *</label>
                <input
                  v-model="conceptForm.date_admission_institution"
                  type="date"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de finalización</label>
                <input
                  v-model="conceptForm.date_finish"
                  type="date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Posgrado / Título</label>
                <input
                  v-model="conceptForm.postitle"
                  type="text"
                  maxlength="191"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej. Especialización en Educación"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Posgrado</label>
                <input
                  v-model="conceptForm.postgraduate"
                  type="text"
                  maxlength="191"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div class="sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-1">Otras formaciones</label>
                <input
                  v-model="conceptForm.other_trainings"
                  type="text"
                  maxlength="191"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div v-if="conceptError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p class="text-sm text-red-600">{{ conceptError }}</p>
            </div>
            <div v-if="conceptSuccess" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p class="text-sm text-green-600">{{ conceptSuccess }}</p>
            </div>
            <div class="flex justify-end gap-3">
              <button
                type="button"
                @click="closeRequestConceptModal"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                :disabled="submittingConcept"
                class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="submittingConcept">Enviando...</span>
                <span v-else>Enviar solicitud</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </template>

    <!-- Vista Admin -->
    <template v-else-if="isAdmin || isExecutive || isDirector">
      <div v-if="loading" class="text-center py-8 text-gray-600">
        Cargando conceptos...
      </div>
      <div v-else-if="loadError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p class="text-red-600">{{ loadError }}</p>
      </div>
      <div v-else>
        <!-- Filtro de año -->
        <div class="mb-6 flex items-center gap-4">
          <label class="text-sm font-medium text-gray-700">Filtrar por año:</label>
          <input
            v-model.number="filterYear"
            type="number"
            min="1900"
            max="2100"
            @change="fetchConcepts"
            class="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 w-32"
          />
        </div>

        <h2 class="text-lg font-semibold text-gray-800 mb-4">Conceptos - Año {{ filterYear }}</h2>
        <div v-if="!adminConcepts.length" class="text-center py-8 text-gray-500">
          No hay conceptos registrados para el año {{ filterYear }}.
        </div>
        <div v-else class="overflow-x-auto border border-gray-200 rounded-lg">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Año</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cargo</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha de solicitud</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nota</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="item in adminConcepts"
                :key="item.id"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-3 text-sm text-gray-900">{{ item.user_name || '-' }}</td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ item.year || '-' }}</td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ item.title || '-' }}</td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ item.position || '-' }}</td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ item.type || '-' }}</td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ formatDate(item.created_at) }}</td>
                <td class="px-4 py-3">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="getStatusClass(item.status)"
                  >
                    {{ statusLabel(item.status) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-900">{{ item.concept || '-' }}</td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      @click="viewConceptDetails(item)"
                      class="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                      title="Ver detalles"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="openGradeConceptModal(item)"
                      class="p-2 text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                      title="Calificar concepto"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="downloadConceptPDF(item)"
                      :disabled="!canDownloadPDF(item)"
                      :class="[
                        'p-2 rounded-md transition-colors',
                        canDownloadPDF(item)
                          ? 'text-green-600 hover:bg-green-50 cursor-pointer'
                          : 'text-gray-300 cursor-not-allowed'
                      ]"
                      :title="canDownloadPDF(item) ? 'Descargar PDF' : 'PDF disponible solo cuando el concepto está listo'"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Calificar concepto (admin) -->
      <div v-if="showGradeConceptModal" class="fixed inset-0 flex items-center justify-center bg-gray-600 bg-opacity-50 z-50">
        <div class="bg-white rounded-lg overflow-hidden shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 class="text-xl font-semibold text-gray-800">Calificar concepto</h2>
            <button
              type="button"
              @click="closeGradeConceptModal"
              class="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div v-if="gradeConceptTarget" class="p-6">
            <!-- Datos del concepto -->
            <div class="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 class="text-sm font-medium text-gray-700 mb-3">Datos del concepto</h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span class="text-gray-500">Profesor:</span> <span class="font-medium">{{ gradeConceptTarget.user_name || '-' }}</span></div>
                <div><span class="text-gray-500">Año:</span> <span class="font-medium">{{ gradeConceptTarget.year || '-' }}</span></div>
                <div><span class="text-gray-500">Título:</span> <span class="font-medium">{{ gradeConceptTarget.title || '-' }}</span></div>
                <div><span class="text-gray-500">Cargo:</span> <span class="font-medium">{{ gradeConceptTarget.position || '-' }}</span></div>
                <div><span class="text-gray-500">Tipo:</span> <span class="font-medium">{{ gradeConceptTarget.type || '-' }}</span></div>
                <div><span class="text-gray-500">Carga horaria:</span> <span class="font-medium">{{ gradeConceptTarget.workload || '-' }}</span></div>
                <div><span class="text-gray-500">Fecha solicitud:</span> <span class="font-medium">{{ formatDate(gradeConceptTarget.created_at) }}</span></div>
                <div><span class="text-gray-500">Estado:</span>
                  <span class="px-2 py-0.5 text-xs font-medium rounded-full" :class="getStatusClass(gradeConceptTarget.status)">
                    {{ statusLabel(gradeConceptTarget.status) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Formulario para agregar/editar nota -->
            <form @submit.prevent="submitGradeConcept">
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Nota / Calificación *</label>
                <select
                  v-model="gradeConceptForm.concept"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="" disabled>Seleccione una calificación...</option>
                  <option v-for="option in gradeOptions" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
              <div v-if="gradeConceptError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p class="text-sm text-red-600">{{ gradeConceptError }}</p>
              </div>
              <div v-if="gradeConceptSuccess" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <p class="text-sm text-green-600">{{ gradeConceptSuccess }}</p>
              </div>
              <div class="flex justify-end gap-3">
                <button
                  type="button"
                  @click="closeGradeConceptModal"
                  class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  :disabled="submittingGrade"
                  class="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="submittingGrade">Guardando...</span>
                  <span v-else>Guardar calificación</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </template>

    <!-- Vista otros roles -->
    <template v-else>
      <p class="text-gray-600">Vista de conceptos.</p>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: "ConceptosView",
  data() {
    return {
      loading: false,
      loadError: '',
      yearlyConcepts: [],
      adminConcepts: [],
      filterYear: new Date().getFullYear() - 1,
      showRequestConceptModal: false,
      showEditConceptModal: false,
      editConceptTarget: null,
      showViewConceptModal: false,
      showGradeConceptModal: false,
      selectedConcept: null,
      gradeConceptTarget: null,
      gradeConceptForm: { concept: '' },
      gradeOptions: [
        '0 - Sin registrar',
        '1 - NO SATISFACTORIO',
        '2 - NO SATISFACTORIO',
        '3 - NO SATISFACTORIO',
        '4 - REGULAR',
        '5 - REGULAR',
        '6 - BUENO',
        '7 - BUENO',
        '8 - MUY BUENO',
        '9 - MUY BUENO',
        '10 - SOBRESALIENTE',
      ],
      gradeConceptError: '',
      gradeConceptSuccess: '',
      submittingConcept: false,
      submittingGrade: false,
      conceptError: '',
      conceptSuccess: '',
      conceptForm: {
        user_id: '',
        year: null,
        title: '',
        postitle: '',
        postgraduate: '',
        other_trainings: '',
        date_admission: '',
        date_finish: '',
        date_admission_institution: '',
        position: '',
        type: '',
        workload: '',
        length_office: '',
        seniority_teacher: '',
      },
    };
  },
  computed: {
    ...mapGetters('authentication', ['currentUser']),
    currentRole() {
      return this.currentUser?.role || '';
    },
    isAdmin() {
      return this.currentRole === 'admin';
    },
    isTeacher() {
      return this.currentRole === 'teacher' || this.currentRole === 'rti' || this.currentRole === 'student_assistant';
    },
    isExecutive() {
      return this.currentRole === 'executive';
    },
    isDirector() {
      return this.currentRole === 'director';
    },
    roleLabel() {
      const labels = {
        admin: 'Administrador',
        teacher: 'Profesor',
        administrative: 'Administrativo',
        executive: 'Ejecutivo',
        rti: 'Rti',        
        director: 'Director',
        student_assistant: 'Preceptor/a',
      };
      return labels[this.currentRole] || this.currentRole || 'Sin rol';
    },
    roleBadgeClass() {
      if (this.isAdmin || this.isExecutive || this.isDirector) return 'bg-indigo-100 text-indigo-800';
      if (this.isTeacher) return 'bg-green-100 text-green-800';
      return 'bg-gray-100 text-gray-800';
    },
    /** Año anterior al actual (4 dígitos) para solicitar concepto */
    previousYear() {
      return new Date().getFullYear() - 1;
    },
    /** Indica si el usuario ya tiene un concepto solicitado para el año anterior */
    hasConceptForPreviousYear() {
      const year = this.previousYear;
      return this.yearlyConcepts.some((c) => String(c.year) === String(year));
    },
  },
  watch: {
    isTeacher: {
      handler(visible) {
        if (visible) this.fetchMyConcepts();
      },
      immediate: true,
    },
    isAdmin: {
      handler(visible) {
        if (visible) this.fetchConcepts();
      },
      immediate: true,
    },
    isExecutive: {
      handler(visible) {
        if (visible) this.fetchConcepts();
      },
      immediate: true,
    },
    isDirector: {
      handler(visible) {
        if (visible) this.fetchConcepts();
      },
      immediate: true,
    },
  },
  methods: {
    fetchMyConcepts() {
      if (!this.isTeacher) return;
      this.loading = true;
      this.loadError = '';
      this.$http
        .get('/my-concepts')
        .then((res) => {
          this.yearlyConcepts = res.data.user_concepts || [];
        })
        .catch((err) => {
          console.error('Error cargando mis conceptos:', err);
          this.loadError = err.response?.data?.message || 'Error al cargar los conceptos.';
          this.yearlyConcepts = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    fetchConcepts() {
      if (!this.isAdmin && !this.isExecutive && !this.isDirector) return;
      this.loading = true;
      this.loadError = '';
      this.$http
        .get('/concepts', {
          params: {
            year: this.filterYear
          }
        })
        .then((res) => {
          // Asumiendo que la respuesta puede tener diferentes estructuras
          this.adminConcepts = res.data.concepts || res.data.user_concepts || res.data || [];
        })
        .catch((err) => {
          console.error('Error cargando conceptos:', err);
          this.loadError = err.response?.data?.message || 'Error al cargar los conceptos.';
          this.adminConcepts = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    canDownloadPDF(item) {
      if (this.isTeacher || this.isExecutive || this.isDirector) {
        return item?.status === 'listo';
      }
      return true;
    },
    /** Indica si el teacher puede editar el concepto (solo si año del concepto = año actual - 1) */
    canEditConcept(item) {
      const editableYear = new Date().getFullYear() - 1;
      return this.isTeacher && item && Number(item.year) === editableYear;
    },
    statusLabel(status) {
      if (status === 'confirmado') return 'Confirmado';
      if (status === 'sinconfirmar') return 'Sin confirmar';
      if (status === 'pendiente') return 'Pendiente';
      if (status === 'listo') return 'Listo';
      return status || '-';
    },
    getStatusClass(status) {
      if (status === 'confirmado') return 'bg-green-100 text-green-800';
      if (status === 'sinconfirmar') return 'bg-amber-100 text-amber-800';
      if (status === 'pendiente') return 'bg-gray-100 text-gray-800';
      if (status === 'listo') return 'bg-green-100 text-green-800';
      return 'bg-gray-100 text-gray-800';
    },
    formatDate(dateTimeString) {
      if (!dateTimeString) return '-';
      // Extraer solo la fecha (YYYY-MM-DD) de un string datetime (YYYY-MM-DDTHH:mm:ss)
      const datePart = dateTimeString.split('T')[0];
      return datePart;
    },
    viewConceptDetails(concept) {
      this.selectedConcept = concept;
      this.showViewConceptModal = true;
    },
    closeViewConceptModal() {
      this.showViewConceptModal = false;
      this.selectedConcept = null;
    },
    openGradeConceptModal(concept) {
      this.gradeConceptTarget = concept;
      this.gradeConceptForm = { concept: concept.concept || '' };
      this.gradeConceptError = '';
      this.gradeConceptSuccess = '';
      this.showGradeConceptModal = true;
    },
    closeGradeConceptModal() {
      this.showGradeConceptModal = false;
      this.gradeConceptTarget = null;
      this.gradeConceptForm = { concept: '' };
      this.gradeConceptError = '';
      this.gradeConceptSuccess = '';
    },
    submitGradeConcept() {
      if (!this.gradeConceptTarget) return;
      this.submittingGrade = true;
      this.gradeConceptError = '';
      this.gradeConceptSuccess = '';
      this.$http
        .put(`/concepts/${this.gradeConceptTarget.id}/concept`, {
          concept: this.gradeConceptForm.concept.trim()
        })
        .then(() => {
          this.gradeConceptSuccess = 'Calificación guardada correctamente.';
          this.fetchConcepts();
          setTimeout(() => this.closeGradeConceptModal(), 1200);
        })
        .catch((err) => {
          this.gradeConceptError =
            err.response?.data?.message || 'Error al guardar la calificación. Intente nuevamente.';
        })
        .finally(() => {
          this.submittingGrade = false;
        });
    },
    downloadConceptPDF(concept) {
      if (!concept?.id) return;

      this.$http
        .get(`/user-concepts/${concept.id}/pdf`, { responseType: 'blob' })
        .then((response) => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const contentDisposition = response.headers?.['content-disposition'];
          const filenameMatch = contentDisposition?.match(/filename\*?=['"]?(?:UTF-8'')?([^'";\n]+)['"]?/i);
          const filename = filenameMatch ? filenameMatch[1].trim() : `concepto-${concept.year || 'descarga'}.pdf`;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(async (err) => {
          console.error('Error al descargar el PDF:', err);
          let msg = 'Error al descargar el PDF.';
          if (err.response?.status === 404) {
            msg = 'El PDF no existe aún. Debe calificar el concepto primero.';
          } else if (err.response?.data) {
            const data = err.response.data;
            if (data instanceof Blob) {
              try {
                const text = await data.text();
                const parsed = JSON.parse(text);
                if (parsed.message) msg = parsed.message;
              } catch (_) {}
            } else if (typeof data === 'object' && data.message) {
              msg = data.message;
            }
          }
          alert(msg);
        });
    },
    openRequestConceptModal() {
      const prevYear = new Date().getFullYear() - 1;
      this.conceptForm = {
        user_id: this.currentUser?.user_id || '',
        year: prevYear,
        title: '',
        postitle: '',
        postgraduate: '',
        other_trainings: '',
        date_admission: '',
        date_finish: '',
        date_admission_institution: '',
        position: '',
        type: '',
        workload: '',
        length_office: '',
        seniority_teacher: '',
      };
      this.conceptError = '';
      this.conceptSuccess = '';
      this.showRequestConceptModal = true;
    },
    closeRequestConceptModal() {
      this.showRequestConceptModal = false;
      this.conceptError = '';
      this.conceptSuccess = '';
    },
    openEditConceptModal(concept) {
      if (!concept || !this.canEditConcept(concept)) return;
      this.editConceptTarget = concept;
      this.conceptForm = {
        user_id: concept.user_id || this.currentUser?.user_id || '',
        year: concept.year || new Date().getFullYear() - 1,
        title: concept.title || '',
        postitle: concept.postitle || '',
        postgraduate: concept.postgraduate || '',
        other_trainings: concept.other_trainings || '',
        date_admission: concept.date_admission ? String(concept.date_admission).slice(0, 10) : '',
        date_finish: concept.date_finish ? String(concept.date_finish).slice(0, 10) : '',
        date_admission_institution: concept.date_admission_institution ? String(concept.date_admission_institution).slice(0, 10) : '',
        position: concept.position || '',
        type: concept.type || '',
        workload: concept.workload || '',
        length_office: concept.length_office || '',
        seniority_teacher: concept.seniority_teacher || '',
      };
      this.conceptError = '';
      this.conceptSuccess = '';
      this.showEditConceptModal = true;
    },
    closeEditConceptModal() {
      this.showEditConceptModal = false;
      this.editConceptTarget = null;
      this.conceptError = '';
      this.conceptSuccess = '';
    },
    submitEditConcept() {
      if (!this.editConceptTarget?.id) return;
      this.submittingConcept = true;
      this.conceptError = '';
      this.conceptSuccess = '';
      const payload = {
        year: Number(this.conceptForm.year) || this.editConceptTarget.year,
        title: this.conceptForm.title,
        postitle: this.conceptForm.postitle || '',
        postgraduate: this.conceptForm.postgraduate || '',
        other_trainings: this.conceptForm.other_trainings || '',
        date_admission: this.conceptForm.date_admission,
        date_admission_institution: this.conceptForm.date_admission_institution,
        position: this.conceptForm.position,
        type: this.conceptForm.type,
        workload: this.conceptForm.workload,
        length_office: this.conceptForm.length_office,
        seniority_teacher: this.conceptForm.seniority_teacher,
        date_finish: this.conceptForm.date_finish || null,
      };
      this.$http
        .put(`/user-concepts/${this.editConceptTarget.id}`, payload)
        .then(() => {
          this.conceptSuccess = 'Concepto actualizado correctamente.';
          this.fetchMyConcepts();
          setTimeout(() => this.closeEditConceptModal(), 1200);
        })
        .catch((err) => {
          this.conceptError = err.response?.data?.message || 'Error al actualizar el concepto. Intente nuevamente.';
        })
        .finally(() => {
          this.submittingConcept = false;
        });
    },
    submitUserConcept() {
      if (!this.currentUser?.user_id) {
        this.conceptError = 'No se pudo identificar al usuario.';
        return;
      }
      this.submittingConcept = true;
      this.conceptError = '';
      this.conceptSuccess = '';
      const year = Number(this.conceptForm.year);
      const payload = {
        user_id: this.conceptForm.user_id,
        year: year >= 1000 && year <= 9999 ? year : new Date().getFullYear() - 1,
        title: this.conceptForm.title,
        date_admission: this.conceptForm.date_admission,
        date_admission_institution: this.conceptForm.date_admission_institution,
        position: this.conceptForm.position,
        type: this.conceptForm.type,
        workload: this.conceptForm.workload,
        length_office: this.conceptForm.length_office,
        seniority_teacher: this.conceptForm.seniority_teacher,
        postitle: this.conceptForm.postitle || '',
        postgraduate: this.conceptForm.postgraduate || '',
        other_trainings: this.conceptForm.other_trainings || '',
      };
      if (this.conceptForm.date_finish) payload.date_finish = this.conceptForm.date_finish;

      this.$http
        .post('/user-concepts', payload)
        .then((res) => {
          this.conceptSuccess = res.data?.message || 'Concepto creado exitosamente.';
          this.fetchMyConcepts();
          setTimeout(() => this.closeRequestConceptModal(), 1500);
        })
        .catch((err) => {
          this.conceptError = err.response?.data?.message || 'Error al crear el concepto. Intente nuevamente.';
        })
        .finally(() => {
          this.submittingConcept = false;
        });
    },
  },
};
</script>

<style scoped>
</style>

